import {
  PresetManager,
  Scheduler,
  SchedulerConfig,
  SchedulerFeaturesConfigType,
  ViewPresetConfig
} from "@bryntum/scheduler-thin";
import { WorkMode } from "@app/models/rest/resource";
import { FilterConfig } from "@bryntum/grid-thin";
import {
  CollectionFilterConfig,
  FieldFilterPickerConfig,
  Store as BryntumStore
} from "@bryntum/core-thin";

export class DiaryDefaults {
  private static skillsStore = new BryntumStore({
    fields: ["id", "name"],
    data: []
  });

  private static zonesStore = new BryntumStore({
    fields: ["id", "name"],
    data: []
  });

  private static teamsStore = new BryntumStore({
    fields: ["id", "name"],
    data: []
  });

  private static resourcesStore = new BryntumStore({
    fields: ["id", "name"],
    data: []
  });

  public static setFilterData(
    skills: any[],
    zones: any[],
    teams?: any[],
    resources?: any[]
  ) {
    // Ensure we have valid arrays and filter out any invalid entries
    const validSkills = (skills || []).filter(
      (skill) => skill && skill.id && skill.description
    );
    const validZones = (zones || []).filter(
      (zone) => zone && zone.id && zone.name
    );
    const validTeams = (teams || []).filter(
      (team) => team && team.id && team.name
    );
    const validResources = (resources || []).filter(
      (resource) => resource && resource.id && resource.name
    );

    const skillData = validSkills.map((skill) => ({
      id: skill.id,
      name: skill.description || "Unknown Skill"
    }));
    const zoneData = validZones.map((zone) => ({
      id: zone.id,
      name: zone.name || "Unknown Zone"
    }));

    // Clear and add new data to stores
    this.skillsStore.removeAll();
    if (skillData.length > 0) {
      this.skillsStore.add(skillData);
    }

    this.zonesStore.removeAll();
    if (zoneData.length > 0) {
      this.zonesStore.add(zoneData);
    }

    // Handle teams if provided
    if (validTeams.length > 0) {
      const teamData = validTeams.map((team) => ({
        id: team.id,
        name: team.name || "Unknown Team"
      }));
      this.teamsStore.removeAll();
      this.teamsStore.add(teamData);
      console.log("Updated diary teams store with:", teamData);
    }

    // Handle resources if provided
    if (validResources.length > 0) {
      const resourceData = validResources.map((resource) => ({
        id: resource.id,
        name: resource.name || "Unknown Resource"
      }));
      this.resourcesStore.removeAll();
      this.resourcesStore.add(resourceData);
      console.log("Updated diary resources store with:", resourceData);
    }

    console.log("Updated diary skills store with:", skillData);
    console.log("Updated diary zones store with:", zoneData);
  }

  public static getSkillsStore() {
    return this.skillsStore;
  }

  public static getZonesStore() {
    return this.zonesStore;
  }

  public static getTeamsStore() {
    return this.teamsStore;
  }

  public static getResourcesStore() {
    return this.resourcesStore;
  }
  public static FILTER_CONFIG: Partial<FilterConfig> = {
    pickerConfig: {
      showAddFilterButton: false,
      dateFormat: "DD/MM/YYYY",

      canDeleteFilter: (filter) => {
        return false;
      },
      getFieldFilterPickerConfig: (
        collectionFilterConfig: CollectionFilterConfig
      ): Partial<FieldFilterPickerConfig> => {
        switch (collectionFilterConfig.property) {
          case "skillText":
          case "zoneText":
          case "team": {
            const fixedFieldResult: any = {
              operatorLocked: true,
              propertyLocked: true,
              // store: this.teamStore,
              operators: {
                string: [
                  {
                    value: "isIncludedIn",
                    text: "Filter by values",
                    argCount: 1
                  }
                ]
              }
            };
            fixedFieldResult.filter = {
              id: `${collectionFilterConfig.property}-isIncludedIn`,
              property: collectionFilterConfig.property,
              operator: "isIncludedIn",
              value: collectionFilterConfig.value,
              disabled: collectionFilterConfig.disabled
            };
            // }
            return fixedFieldResult;
          }
        }
        return {};
      }
    }
  };
  public static DEFAULT_DIARY_SETTINGS: Partial<SchedulerConfig> = {
    barMargin: 5,
    rowHeight: 50,
    columnLines: true,
    eventStyle: "filled",
    // Must match the schedule view's stateId so filters carry across the two
    // screens (#1448). If a reset is needed, bump both together.
    // v9: reset stale persisted state that broke diary tile rendering (#1516).
    stateId: "planner-view-v9",
    enableEventAnimations: true,
    multiEventSelect: false,
    scrollable: true,
    forceFit: true,
    autoHeight: false,
    fillTicks: false,
    zoomOnMouseWheel: false,
    zoomOnTimeAxisDoubleClick: false,
    viewPreset: "hour",
    cls: "sch-container",
    style: "height: calc(100vh - 88px); width: calc(100vw)",
    columns: [
      {
        id: "resource",
        type: "resourceInfo",
        locked: true,
        draggable: false,
        text: "Resource",
        showEventCount: false,
        showRole: false,
        editor: false,
        width: 240,
        filterable: {
          filterField: {
            type: "combo",
            multiSelect: true,
            valueField: "id",
            displayField: "name",
            placeholder: "Filter by resources...",
            store: DiaryDefaults.resourcesStore
          },
          // Custom filtering function to handle the resource structure
          filterFn: ({ value, record }) => {
            if (!value || value.length === 0) return true;

            // Check if the resource's id matches the selected filter values
            return value.includes(record.id);
          }
        }
      },
      {
        id: "team",
        text: "Team",
        width: 200,
        field: "team",
        hidden: true,
        renderer: ({ record }) => {
          if (record.team) {
            return record.team;
          }
          return "";
        },
        filterable: {
          filterField: {
            type: "combo",
            multiSelect: true,
            valueField: "id",
            displayField: "name",
            placeholder: "Filter by teams...",
            store: DiaryDefaults.teamsStore
          },
          // Custom filtering function to handle the team structure
          filterFn: ({ value, record }) => {
            if (!value || value.length === 0) return true;

            // In diary viewer, team is stored as a string, so we need to match by name
            if (!record.team) return false;

            // Get team names from the store for the selected IDs
            const selectedTeamNames = value
              .map((teamId) => {
                const teamRecord = DiaryDefaults.teamsStore.getById(teamId);
                return teamRecord ? teamRecord.get("name") : null;
              })
              .filter((name) => name !== null);

            return selectedTeamNames.includes(record.team);
          }
        }
      },
      {
        id: "skills",
        width: 360,
        text: "Skills",
        field: "skillText",
        hidden: true,
        renderer: ({ record }) => {
          return record.skillText || "";
        },
        filterable: {
          filterField: {
            type: "combo",
            multiSelect: true,
            valueField: "id",
            displayField: "name",
            placeholder: "Filter by skills...",
            store: DiaryDefaults.skillsStore
          },
          // Custom filtering function to handle the skillText field
          filterFn: ({ value, record }) => {
            if (!value || value.length === 0) return true;

            if (!record.skillText) return false;

            // Get skill names from the store for the selected IDs
            const selectedSkillNames = value
              .map((skillId) => {
                const skillRecord = DiaryDefaults.skillsStore.getById(skillId);
                return skillRecord ? skillRecord.get("name") : null;
              })
              .filter((name) => name !== null);

            console.log(
              "Diary skill filter - Selected skill names:",
              selectedSkillNames
            );
            console.log(
              "Diary skill filter - Record skillText:",
              record.skillText
            );

            // Check if any of the selected skills are in the skillText string
            const matches = selectedSkillNames.some((skillName) =>
              record.skillText.includes(skillName)
            );

            console.log("Diary skill filter - Matches:", matches);
            return matches;
          }
        }
      },
      {
        id: "zones",
        text: "Zones",
        field: "zoneText",
        hidden: true,
        width: 200,
        renderer: ({ record }) => {
          return record.zoneText || "";
        },
        filterable: {
          filterField: {
            type: "combo",
            multiSelect: true,
            valueField: "id",
            displayField: "name",
            placeholder: "Filter by zones...",
            store: DiaryDefaults.zonesStore
          },
          // Custom filtering function to handle the zoneText field
          filterFn: ({ value, record }) => {
            if (!value || value.length === 0) return true;

            if (!record.zoneText) return false;

            // Get zone names from the store for the selected IDs
            const selectedZoneNames = value
              .map((zoneId) => {
                const zoneRecord = DiaryDefaults.zonesStore.getById(zoneId);
                return zoneRecord ? zoneRecord.get("name") : null;
              })
              .filter((name) => name !== null);

            // Check if any of the selected zones are in the zoneText string
            return selectedZoneNames.some((zoneName) =>
              record.zoneText.includes(zoneName)
            );
          }
        }
      },
      {
        id: "workMode",
        text: "Mode",
        field: "workMode",
        hidden: true,
        width: 50,
        renderer: ({ record }) =>
          record.workMode && record.workMode === WorkMode.Offline
            ? "Offline"
            : "Online"
      }
    ],
    features: null
  };
  public static DEFAULT_DIARY_FEATURES: Partial<SchedulerFeaturesConfigType> = {
    rowCopyPaste: false,
    eventCopyPaste: false,
    eventDrag: false,
    eventDragCreate: {},
    eventEdit: false,
    columnResize: true,
    regionResize: true,
    eventFilter: true,
    eventResize: true,
    eventTooltip: false,
    scheduleTooltip: false,
    timeRanges: { showHeaderElements: true, showCurrentTimeLine: true },
    resourceTimeRanges: true,
    cellEdit: false,
    filter: DiaryDefaults.FILTER_CONFIG,
    scheduleMenu: {
      items: {
        addItem: true
      }
    },
    eventMenu: {
      items: {
        deleteEvent: true,
        unassignEvent: false
      }
    },
    cellMenu: {
      items: {
        removeRow: false
      }
    },
    timeAxisHeaderMenu: {
      items: {
        zoomLevel: false,
        dateRange: false
      }
    }
  };

  public static Create(config, features): any {
    let mergedConfig: any = DiaryDefaults.DEFAULT_DIARY_SETTINGS;
    mergedConfig = Object.assign(mergedConfig, config);
    mergedConfig.features = DiaryDefaults.DEFAULT_DIARY_FEATURES;
    mergedConfig.features = Object.assign(mergedConfig.features, features);

    const preset: Partial<ViewPresetConfig> = {
      name: "weekAndDay",
      displayDateFormat: "HH:mm",
      shiftIncrement: 1,
      shiftUnit: "day",
      mainHeaderLevel: 1,
      timeResolution: {
        unit: "minute",
        increment: 5
      },
      columnLinesFor: 1,
      headers: [
        {
          unit: "hour",
          headerCellCls: "minute-tick",
          dateFormat: "HH:mm"
        },
        {
          unit: "minute",
          increment: 15,
          dateFormat: "",
          headerCellCls: "minute-tick"
        }
      ]
    };
    PresetManager.registerPreset("hour", preset);

    return new Scheduler(mergedConfig);
  }
}
